const createGenericId = (id: string) => `generic-${id}`;
const createSpecificId = (id: string) => `specific-${id}`;
const createFormElementId = (formName: string, inputName: string) => `form-${formName}-${inputName}`;

export const TEST_IDS = {
  helpers: {
    createReportBlockId: (id: string) => createSpecificId(`report-block-${id}`),
    createTableId: (id: string) => createSpecificId(`table-${id}`),
    createTableCellId: (id: string) => createGenericId(`table-cell-${id}`),
    createListItemId: (id: string) => createGenericId(`list-item-${id}`),
    createFormElementId: (formName: string, inputName: string) => createFormElementId(formName, inputName),
    createDropdownOptionId: (id: string, index: number) => createGenericId(`dropdown-option-${id}-${index}`),
    createFeatureOnboardingElementId: (id: string) => createSpecificId(`feature-onboarding-${id}`),
    createChartCanvasId: (id: string) => createGenericId(`chart-canvas-${id}`),
    createToggleId: (name: string) => createGenericId(`toggle-${name}`),
  },
  // Can appear multiple times inside the DOM should only be selected inside a specific context
  generic: {
    primaryLabel: createGenericId('primary-label'),
    filters: {
      dropdownContainer: createGenericId('filters-dropdown-container'),
      list: createGenericId('filters-list'),
      toggleBtn: createGenericId('toggle-filters-btn'),
      newFilterTabBtn: createGenericId('new-filter-tab-btn'),
      filterTemplatesTabBtn: createGenericId('filter-templates-tab-btn'),
      filterTemplatesList: createGenericId('filter-templates-list'),
      appliedFilterChip: createGenericId('applied-filter-chip'),
      appliedFilterChipRemoveBtn: createGenericId('applied-filter-chip-remove-btn'),
      operatorBtn: createGenericId('filter-operator-btn'),
      operatorOption: createGenericId('filter-operator-option'),
    },
    banner: {
      warning: 'warning-banner',
      danger: 'danger-banner',
    },
    bulkOperations: {
      selectBtn: createGenericId('bulk-operations-select-btn'),
      selectAll: createGenericId('bulk-operations-select-all'),
    },
    buttons: {
      close: createGenericId('close-btn'),
      open: createGenericId('open-btn'),
      submit: createGenericId('submit-btn'),
      submitAndPreserve: createGenericId('submit-and-preserve-btn'),
      apply: createGenericId('apply-btn'),
      continue: createGenericId('continue-btn'),
      cancel: createGenericId('cancel-btn'),
      reset: createGenericId('reset-btn'),
      erase: createGenericId('erase-btn'),
      refresh: createGenericId('refresh-btn'),
      back: createGenericId('back-btn'),
      next: createGenericId('next-btn'),
      upgrade: createGenericId('upgrade-btn'),
      closeModal: createGenericId('close-modal-btn'),
      seeMore: createGenericId('see-more-btn'),
      viewAll: createGenericId('view-all-btn'),
      play: createGenericId('play-btn'),
      replay: createGenericId('replay-btn'),
      pause: createGenericId('pause-btn'),
      create: createGenericId('create-btn'),
      select: createGenericId('select-btn'),
      copyToClipboard: createGenericId('copy-to-clipboard-btn'),
      goToWebsite: createGenericId('go-to-website-btn'),
      redirect: createGenericId('redirect-btn'),
      jumpToEvent: createGenericId('jump-to-event-btn'),
      goToPrevWebsite: createGenericId('go-to-prev-website-btn'),
      contact: createGenericId('email-contact-btn'),
      delete: createGenericId('delete-btn'),
      modalCancel: createGenericId('modal-cancel-btn'),
      modalApply: createGenericId('modal-apply-btn'),
      sidebarToggle: createGenericId('sidebar-toggle-btn'),
    },
    toggle: createGenericId('toggle-switch'),
    visualizationOptions: {
      dropdownContainer: createGenericId('visualization-options-dropdown-container'),
      toggleBtn: createGenericId('toggle-visualization-options-btn'),
      overallAverage: createGenericId('overall-average'),
    },
    dropdown: createGenericId('dropdown'),
    datePicker: {
      dropdownContainer: createGenericId('date-picker-dropdown-container'),
      optionsList: createGenericId('date-picker-options-list'),
      weekDaysList: createGenericId('date-picker-week-days-list'),
      header: createGenericId('date-picker-header'),
      daysPanel: createGenericId('date-picker-days-panel'),
      monthsPanel: createGenericId('date-picker-months-panel'),
      yearsPanel: createGenericId('date-picker-years-panel'),
      toggleMonthSelectorBtn: createGenericId('date-picker-toggle-month-selector-btn'),
      toggleYearSelectorBtn: createGenericId('date-picker-toggle-year-selector-btn'),
      prevMonthArrow: createGenericId('date-picker-prev-month-arrow'),
      nextMonthArrow: createGenericId('date-picker-next-month-arrow'),
      prevYearArrow: createGenericId('date-picker-prev-year-arrow'),
      nextYearArrow: createGenericId('date-picker-next-year-arrow'),
      leftPanel: createGenericId('date-picker-left-panel'),
      rightPanel: createGenericId('date-picker-right-panel'),
      dayOption: (day: number) => createGenericId(`date-picker-options-day-${day}`),
      yearOption: (year: number) => createGenericId(`date-picker-options-year-${year}`),
    },
    inputs: {
      search: createGenericId('search-input'),
      email: createGenericId('email-input'),
      password: createGenericId('password-input'),
      text: createGenericId('text-input'),
      checkbox: createGenericId('checkbox-input'),
      textContainer: createGenericId('text-input-container'),
    },
    export: {
      toggleBtn: createGenericId('toggle-export-btn'),
      dropdownContainer: createGenericId('export-dropdown-container'),
      getExportOptionId: (option: string) => createGenericId(`export-option-${option}`),
    },
    previousPeriodComparison: {
      comparisonItem: createGenericId('previous-period-comparison-item'),
      toggleOptionsDropdownBtn: createGenericId('toggle-previous-period-comparison-options-dropdown'),
      toggleComparisonBtn: createGenericId('toggle-previous-period-comparison-btn'),
      dropdownOptionsContainer: createGenericId('compare-to-previous-dropdown'),
    },
    chart: {
      chartTileInfo: {
        label: createGenericId('chart-tile-label'),
        heading: createGenericId('chart-tile-heading'),
        subheading: createGenericId('chart-tile-sub-heading'),
        percent: createGenericId('chart-tile-percent'),
      },
      chartTileCta: createGenericId('chart-tile-cta'),
      getChartTileId: (id: string) => createGenericId(`chart-tile-${id}`),
      toggleTileContextMenuBtn: createGenericId('chart-toggle-tile-context-menu-btn'),
      tooltipContainer: createGenericId('chart-tooltip-container'),
      datapoint: createGenericId('chart-datapoint'),
    },
    table: {
      nextPageBtn: createGenericId('table-next-page-btn'),
      prevPageBtn: createGenericId('table-prev-page-btn'),
      pageSelectorDropdown: createGenericId('table-page-selector-dropdown'),
      pageSelectorTooltip: createGenericId('table-page-selector-tooltip'),
      pageSelectorInputField: createGenericId('table-page-selector-input-field'),
      rowsPerPageDropdown: createGenericId('table-rows-per-page-dropdown'),
      rowsPerPageTooltip: createGenericId('table-rows-per-page-tooltip'),
      rowsPerPageOption: createGenericId('table-rows-per-page-option'),
      goToFirstPageBtn: createGenericId('table-go-to-first-page-btn'),
      goToLastPageBtn: createGenericId('table-go-to-last-page-btn'),
      sortHeaderBtn: createGenericId('table-sorting-header-btn'),
      groupHeaderBtn: createGenericId('table-grouping-header-btn'),
      sortAndGroupHeaderBtn: createGenericId('table-sorting-and-grouping-header-btn'),
      groupColumnSwitch: createGenericId('table-group-column-switch'),
      groupColumnOption: createGenericId('table-group-column-option'),
      headerOptionsTooltip: createGenericId('table-header-options-tooltip'),
      sortColumnAscBtn: createGenericId('table-sort-column-asc-btn'),
      sortColumnDescBtn: createGenericId('table-sort-column-desc-btn'),
      paginationSummary: createGenericId('table-pagination-summary'),
    },
    dashboardCard: {
      heading: createGenericId('dashboard-card-heading'),
      percentageChange: createGenericId('dashboard-card-percentage-change'),
    },
    links: {
      location: createGenericId('links-location'),
      visitedPagesHistory: createGenericId('link-visited-pages-history'),
      sessionRecordings: createGenericId('link-session-recordings'),
      userHistory: createGenericId('link-user-history'),
      action: createGenericId('link-user-action'),
      seeMore: createGenericId('link-see-more'),
      goToPage: createGenericId('link-go-to-page'),
    },
    companyReveal: {
      label: createGenericId('company-reveal-label'),
      tooltipContent: createGenericId('company-reveal-tooltip-content'),
    },
    visitInfo: {
      device: createGenericId('visit-info-device'),
      browser: createGenericId('visit-info-browser'),
      os: createGenericId('visit-info-os'),
    },
    featureCard: {
      statusSection: createGenericId('feature-card-status-section'),
      toggleStatusBtn: createGenericId('feature-card-toggle-status-btn'),
      viewUnseenRecordingsBtn: createGenericId('feature-card-view-unseen-recordings'),
      settingsBtn: createGenericId('feature-card-settings-btn'),
    },
    privacyLevelCard: {
      countriesDropdown: createGenericId('privacy-level-card-countries-dropdown'),
      featuresCard: createGenericId('privacy-level-card-features-card'),
    },
    eventTracking: {
      toggleSettingBtn: createGenericId('event-tracking-toggle-settings-btn'),
      autoTrackingCard: createGenericId('auto-tracking-card'),
    },
    whiteLabel: {
      toggleWhiteLabelSwitch: createGenericId('white-label-toggle-white-label-switch'),
      pricingPlanBtn: createGenericId('white-label-pricing-plan-btn'),
      upgradePlanBtn: createGenericId('white-label-upgrade-plan-btn'),
      commissionPayoutsBtn: createGenericId('white-label-commission-payouts-btn'),
      payoutsByYearSelect: createGenericId('white-label-payouts-by-year-select'),
    },
    whiteLabelTheme: {
      confirmReplyToEmailBtn: createGenericId('custom-email-confirm-reply-to-email-btn'),
      sendTestEmailBtn: createGenericId('custom-email-send-test-email-btn'),
    },
    invoices: {
      downloadInvoiceBtn: createGenericId('download-invoice-btn'),
    },
    icon: {
      os: createGenericId('icon-os'),
      browser: createGenericId('icon-browser'),
      device: createGenericId('icon-device'),
      lock: createGenericId('icon-lock'),
      flag: createGenericId('icon-flag'),
      utm: createGenericId('icon-utm'),
    },
    visitorsMap: {
      togglePinsBtn: createGenericId('visitors-map-toggle-pins-btn'),
      liveMapContent: createGenericId('visitors-live-map-content'),
      liveMapTooltip: createGenericId('visitors-live-map-tooltip'),
      liveMapCounter: createGenericId('visitors-live-map-counter'),
    },

    timeUnitSelector: createGenericId('time-unit-selector'),
    funnels: {
      detailsBtn: createGenericId('funnels-details-btn'),
      activateBtn: createGenericId('funnels-activate-btn'),
      moreActionsBtn: createGenericId('funnels-more-actions-btn'),
      editBtn: createGenericId('funnels-edit-name-btn'),
      editNameBtn: createGenericId('funnels-edit-name-btn'),
      copyLinkBtn: createGenericId('funnels-copy-link-btn'),
      deleteBtn: createGenericId('funnels-delete-btn'),
      editor: {
        funnelNameInput: createGenericId('funnel-editor-funnel-name-input'),
        funnelConversionRateInput: createGenericId('funnel-editor-funnel-conversion-rate-input'),
        toggleLevelForm: createGenericId('funnel-editor-toggle-level-form'),
        addLevelBtn: createGenericId('funnel-editor-add-level-btn'),
        removeLevelBtn: createGenericId('funnel-editor-remove-level-btn'),
        levelForm: {
          levelNameInput: createGenericId('funnel-editor-level-name-input'),
          levelKindSelector: createGenericId('funnel-editor-level-kind-selector'),
          pageSelector: createGenericId('funnel-editor-page-selector'),
          urlPatternSelector: createGenericId('funnel-editor-url-pattern-selector'),
          eventKind: {
            categoryInput: createGenericId('funnel-editor-event-category-input'),
            actionInput: createGenericId('funnel-editor-event-action-input'),
            labelInput: createGenericId('funnel-editor-event-label-input'),
            valueInput: createGenericId('funnel-editor-event-value-input'),
            fieldsObjectInput: createGenericId('funnel-editor-event-fields-object-input'),
          },
          ecomKind: {
            eventTypeSelector: createGenericId('funnel-editor-ecom-event-type-selector'),
          },
          abeKind: {
            eventTypeSelector: createGenericId('funnel-editor-abe-event-type-selector'),
          },
          outgoingTrafficKind: {
            externalUrlInput: createGenericId('funnel-editor-outgoing-traffic-external-url-input'),
          },
        },
      },
      detailsPage: {
        levelDiagramSection: createGenericId('funnel-details-level-diagram-section'),
      },
    },
    websiteUsabilityKpi: {
      watchSessionRecordingsBtn: createGenericId('website-usability-kpi-watch-session-recordings-btn'),
      viewContainingPagesBtn: createGenericId('website-usability-kpi-view-containing-pages-btn'),
    },
    websiteActions: {
      viewTrackingCodeBtn: createGenericId('website-actions-view-tracking-code-btn'),
      jumpToDashboardBtn: createGenericId('website-actions-jump-to-dashboard-btn'),
      manageWebsiteContributorsBtn: createGenericId('website-actions-manage-website-contributors-btn'),
      websiteSettingsBtn: createGenericId('website-actions-settings-btn'),
      copyWebsiteIdBtn: createGenericId('website-actions-copy-website-id-btn'),
      applyWhiteLabelBtn: createGenericId('website-actions-apply-white-label-btn'),
      removeWebsiteBtn: createGenericId('website-actions-remove-website-btn'),
      contactOwnerBtn: createGenericId('website-actions-contact-owner-btn'),
      leaveWebsiteBtn: createGenericId('website-actions-leave-website-btn'),
      applySumoLifetimeDeal: createGenericId('website-actions-apply-sumo-lifetime-deal'),
    },
    heatmaps: {
      toggleBtn: createGenericId('toggle-heatmaps-btn'),
      createBtn: createGenericId('create-heatmap-btn'),
      heatmapCard: {
        selectHeatmapBtn: createGenericId('heatmap-card-select-btn'),
        settingsMenuBtn: createGenericId('heatmap-card-settings-menu-btn'),
        heatmapCreator: createGenericId('heatmap-card-creator'),
        heatmapStatus: createGenericId('heatmap-card-status'),
        heatmapLastEdited: createGenericId('heatmap-card-last-edited'),
        pageTargetType: createGenericId('heatmap-card-page-target-type'),
        pageUrl: createGenericId('heatmap-card-page-url'),
        pageViews: createGenericId('heatmap-card-page-views'),
        viewHeatmapBtn: createGenericId('heatmap-card-view-heatmap-btn'),
      },
      createHeatmap: {
        selectPage: createGenericId('create-heatmap-select-page'),
        pageSearchField: createGenericId('create-heatmap-page-search-field'),
      },
    },
    recordings: {
      starIcon: createGenericId('recordings-star-icon'),
      commentIcon: createGenericId('recordings-comment-icon'),
      player: {
        root: 'ssr-player',
        frame: 'ssr-player-frame',
        timeline: 'ssr-player-timeline',
        timelineSegment: 'ssr-player-timeline-segment',
        getControlBtnId: (action: string) => `ssr-player-${action}-btn`,
      },
    },
    trendIntel: {
      websiteSettingField: createGenericId('trend-intel-website-setting-field'),
      contentOpportunityStatusDropdown: createGenericId('content-opportunity-status-dropdown'),
      generateCategoryRow: createGenericId('generate-category-row'),
      generateCategoryIncrementBtn: createGenericId('generate-category-increment-btn'),
      generateCategoryDecrementBtn: createGenericId('generate-category-decrement-btn'),
      generateSubmitBtn: createGenericId('generate-submit-btn'),
      generateAllocationCount: createGenericId('generate-allocation-count'),
      generateResetBtn: createGenericId('generate-reset-btn'),
      generateUseAllTopSuggestionsBtn: createGenericId('generate-use-all-top-suggestions-btn'),
      generateAwaitingFirstTopicsMessage: createGenericId('generate-awaiting-first-topics-message'),
      contentOpportunityDraftButton: createGenericId('content-opportunity-draft-button'),
      contentOpportunityAssignee: createGenericId('content-opportunity-assignee'),
      contentActionsMenuButton: createGenericId('content-actions-menu-button'),
      contentActionsEditTitleOption: createGenericId('content-actions-edit-title-option'),
      contentActionsCopyLinkOption: createGenericId('content-actions-copy-link-option'),
      customDraftStatusDropdown: createGenericId('custom-draft-status-dropdown'),
      createCustomDraftButton: createGenericId('create-custom-draft-button'),
      overviewCardTopicRow: createGenericId('trend-intel-overview-card-topic-row'),
      overviewCardTopicCategoryIcon: createGenericId('trend-intel-overview-card-topic-category-icon'),
      overviewCardTopicActionButton: createGenericId('trend-intel-overview-card-topic-action-button'),
    },
    menu: {
      deleteOption: createGenericId('menu-delete-option'),
    },
    link: createGenericId('link'),
    title: createGenericId('title'),
    subtitle: createGenericId('subtitle'),
    tooltipTitle: createGenericId('tooltip-title'),
    tooltipSubtitle: createGenericId('tooltip-subtitle'),
    tooltipText: createGenericId('tooltip-text'),
    tooltipContent: createGenericId('tooltip-content'),
    nestedNavContextMenu: createGenericId('nav-link-context-menu'),
    validationRepresentation: {
      error: createGenericId('validation-error'),
    },
    loader: createGenericId('loader'),
    count: createGenericId('count'),
    avatar: createGenericId('avatar'),
    noDataAvailable: createGenericId('no-data-available'),
    progress: createGenericId('progress'),
    accountSettings: {
      deleteAccountBtn: createGenericId('account-settings-delete-account-btn'),
    },
  },
  // Should only appear once in the DOM and can be selected directly
  specific: {
    appLogo: createSpecificId('app-logo'),
    featureTourBtn: createSpecificId('feature-tour-btn'),
    toggleAccountSettingsBtn: createSpecificId('toggle-account-settings-btn'),
    accountMenuTrigger: createSpecificId('account-menu-trigger'),
    toggleLeftSidebarBtn: createSpecificId('toggle-left-sidebar-btn'),
    pageTitle: createSpecificId('page-title'),
    pageHeaderContainer: createSpecificId('page-header-container'),
    aiAssistant: createSpecificId('ai-assistant-btn'),
    banner: {
      conversions: createSpecificId('conversions-banner'),
      customEvents: createSpecificId('custom-events-banner'),
    },
    autoRefreshDropdown: {
      toggleBtn: createSpecificId('toggle-auto-refresh-dropdown-btn'),
      dropdownContainer: createSpecificId('auto-refresh-dropdown-container'),
    },
    featureCounter: {
      btn: createSpecificId('feature-counter-btn'),
      dropdownContainer: createSpecificId('feature-counter-dropdown-container'),
      limit: createSpecificId('feature-counter-limit'),
    },
    customDashboards: {
      createDashboardFormContainer: createSpecificId('create-dashboard-form-container'),
      createDashboardBtn: createSpecificId('create-dashboard-btn'),
      toggleAllDashboardsDropdown: createSpecificId('toggle-all-dashboard-btn'),
      dashboardsListDropdownContainer: createSpecificId('custom-dashboards-list-dropdown-container'),
      getOpenDashboardLinkId: (id: string) => createSpecificId(`open-dashboard-link-${id}`),
    },
    datePicker: {
      toggleBtn: createSpecificId('date-picker-toggle-btn'),
      dropdownContainer: createSpecificId('date-picker-dropdown-container'),
    },
    nestedNavigationLinks: {
      masterDashboard: createSpecificId('master-dashboard-nav-link'),
      pagesDashboard: createSpecificId('pages-dashboard-nav-link'),
      realTimeAnalytics: createSpecificId('real-time-analytics-nav-link'),
      eventTracking: {
        auto: createSpecificId('event-auto-tracking-nav-link'),
        manual: createSpecificId('event-manual-tracking-nav-link'),
        alarmingBehavior: createSpecificId('event-alarming-behavior-nav-link'),
        generator: createSpecificId('event-tag-generator-nav-link'),
      },
      trafficStructure: {
        trafficChannels: createSpecificId('traffic-channels-nav-link'),
        pagesPerSession: createSpecificId('pages-per-session-nav-link'),
        sessionDuration: createSpecificId('session-duration-nav-link'),
        bounceRate: createSpecificId('bounce-rate-nav-link'),
        advancedBounceRate: createSpecificId('advanced-bounce-rate-nav-link'),
        internalTraffic: createSpecificId('internal-traffic-nav-link'),
      },
      pages: {
        landingPages: createSpecificId('landing-pages-nav-link'),
        conversions: createSpecificId('conversion-pages-nav-link'),
        referring: createSpecificId('referring-pages-nav-link'),
        internalURLReferrers: createSpecificId('internal-url-referrers-nav-link'),
      },
      conversionsSettings: {
        overview: createSpecificId('conversions-settings-overview-nav-link'),
      },
      conversionEvents: {
        overview: createSpecificId('conversion-events-overview-nav-link'),
        byReferrer: createSpecificId('conversion-events-by-referrer-nav-link'),
        byTraffic: createSpecificId('conversion-events-by-traffic-nav-link'),
        byDevice: createSpecificId('conversion-events-by-device-nav-link'),
        byBrowser: createSpecificId('conversion-events-by-browser-nav-link'),
        byResolutions: createSpecificId('conversion-events-by-resolutions-nav-link'),
        byCountry: createSpecificId('conversion-events-by-country-nav-link'),
        byCities: createSpecificId('conversion-events-by-cities-nav-link'),
        byFunnels: createSpecificId('conversion-events-by-funnels-nav-link'),
        byCampaign: createSpecificId('conversion-events-by-campaign-nav-link'),
      },
      trackingCode: {
        manual: createSpecificId('tracking-code-manual-nav-link'),
        native: createSpecificId('tracking-code-native-nav-link'),
      },
    },
    admin3as: {
      pricingPlansNavLink: createSpecificId('admin3as-pricing-plans-nav-link'),
      clientsNavLink: createSpecificId('admin3as-clients-nav-link'),
      maxStpLimitBtn: createSpecificId('admin3as-max-stp-limit-btn'),
      stpCostBtn: createSpecificId('admin3as-stp-cost-btn'),
    },
    register: {
      form: createSpecificId('register-form'),
    },
    recordingDetailsPage: {
      detailsTab: createSpecificId('recording-details-tab'),
      actionsTab: createSpecificId('recording-actions-tab'),
    },
    recordingsSettingsTab: {
      recordAllPagesBtn: createSpecificId('recordings-settings-record-all-pages-btn'),
      recordSpecificPages: {
        getButtonId: (type: 'include' | 'exclude') =>
          createSpecificId(`recordings-settings-${type}-specific-pages-btn`),
        selectPageTab: createSpecificId('recordings-settings-record-specific-pages-select-page-tab'),
        selectPageDropdown: createSpecificId('recordings-settings-record-specific-pages-select-page-dropdown'),
        selectCustomUrlTab: createSpecificId('recordings-settings-record-specific-pages-select-custom-url-tab'),
        urlStructurePreviewBtn: createSpecificId('recordings-settings-record-specific-pages-url-structure-preview-btn'),
        selectUrlPatternDropdown: createSpecificId(
          'recordings-settings-record-specific-pages-select-url-pattern-dropdown',
        ),
      },
      minDurationSwitch: createSpecificId('recordings-settings-min-duration-switch'),
      minDurationInput: createSpecificId('recordings-settings-min-duration-input'),
      textObfuscationSwitch: createSpecificId('recordings-settings-text-obfuscation-switch'),
    },
    outgoingTrafficSettingsTab: {
      considerWhitelistedURLsAsExternalToggle: createSpecificId(
        'outgoingTraffic-settings-whitelistedURLs-external-toggle',
      ),
    },
    wixMini: {
      recordingsOverviewNavLink: createSpecificId('wix-mini-recordings-overview-nav-link'),
      generalSettingsNavLink: createSpecificId('wix-mini-general-settings-nav-link'),
      privacyCenterNavLink: createSpecificId('wix-mini-privacy-center-nav-link'),
      alarmingBehaviorNavLink: createSpecificId('wix-mini-alarming-behavior-nav-link'),
    },
    activateSessionRecordings: createSpecificId('activate-session-recordings'),

    upgradePlan: {
      monthlyOptionTabButton: createSpecificId('monthly-option-tab-button'),
      yearlyOptionTabButton: createSpecificId('yearly-option-tab-button'),
    },
    conversions: {
      createConversion: {
        selectCategory: createSpecificId('create-conversion-select-category'),
        selectPage: createSpecificId('create-conversion-select-page'),
        selectFunnel: createSpecificId('create-conversion-select-funnel'),
        selectEventType: createSpecificId('create-conversion-select-event-type'),
        pageSearchField: createSpecificId('create-conversion-page-search-field'),
        selectCustomUrl: createSpecificId('create-conversion-page-select-custom-url'),
        addQueryParam: createSpecificId('create-conversion-add-query-param'),
        removeQueryParam: (index: number) => createSpecificId(`create-conversion-remove-query-param-${index}`),
        inputs: {
          name: createFormElementId('create-conversion', 'name'),
          url: createFormElementId('create-conversion', 'url'),
          conversionValue: createFormElementId('create-conversion', 'conversion-value'),
          conversionGoal: createFormElementId('create-conversion', 'conversion-goal'),
          note: createFormElementId('create-conversion', 'note'),
          autocomplete: (name: string) => createFormElementId('create-conversion', name),
          queryParamName: (index: number) => createFormElementId('create-conversion', `query-param-name-${index}`),
          queryParamValue: (index: number) => createFormElementId('create-conversion', `query-param-value-${index}`),
        },
      },
    },
    trendIntel: {
      websiteContext: createSpecificId('trend-intel-website-context'),
      trendCategoryDistribution: createSpecificId('trend-intel-trend-category-distribution'),
      trendCategoryDistributionList: createSpecificId('trend-intel-trend-category-distribution-list'),
      contentOpportunitiesSortDropdown: createSpecificId('content-opportunities-sort-dropdown'),
      discoverContentOpportunitiesButton: createSpecificId('discover-content-opportunities-button'),
      customDraftsSortDropdown: createSpecificId('custom-drafts-sort-dropdown'),
      trendingTopicsWaitingState: createSpecificId('trend-intel-trending-topics-waiting-state'),
      overviewCard: {
        activateButton: createSpecificId('trend-intel-overview-card-activate-button'),
        exploreAllTopicsButton: createSpecificId('trend-intel-overview-card-explore-all-topics-button'),
        topicsList: createSpecificId('trend-intel-overview-card-topics-list'),
        noTopicsMessage: createSpecificId('trend-intel-overview-card-no-topics-message'),
      },
    },
    funnels: {
      detailsPage: {
        sessionsInSelectedPeriod: createSpecificId('funnel-details-sessions-in-selected-period'),
        actualConversionRate: createSpecificId('funnel-details-actual-conversion-rate'),
        sessionsConvertedInSelectedPeriod: createSpecificId('funnel-details-sessions-converted-in-selected-period'),
        expectedConversionRate: createSpecificId('funnel-details-expected-conversion-rate'),
        funnelName: createSpecificId('funnel-details-funnel-name'),
        funnelLevelCount: createSpecificId('funnel-details-funnel-levels-count'),
        createdOn: createSpecificId('funnel-details-created-on'),
        createdBy: createSpecificId('funnel-details-created-by'),
      },
    },
  },
};
